import { Box, Typography } from '@mui/material';
import React from 'react';

import {
  type ProcessTimelineTone,
  processTimelineParts,
  processTimelineTone,
} from '../../pages/Processes/processDisplay';

/**
 * ONE timeline, rendered the same way everywhere it appears.
 *
 * Owner, 2026-09-10: *"统一，以 worker web 那套为准"* — after finding the admin page, the worker portal and
 * the phone drawing one concept three ways. The WORDS were unified earlier the same day
 * (`visibleTimelineEvents` / `processTimelineParts` / `timelineStepLabel`); this is the chrome, and it is
 * the last of the three layers to stop being per-page.
 *
 * WHAT WAS MEASURED BEFORE WRITING IT:
 *
 *   order    identical on all three — the backend sorts by `created_at` and NOT ONE client re-sorts.
 *            So the ordering needed no work, and touching it would have been risk with no gain.
 *   chrome   worker portal had dot + rail; the admin page had a fixed-width timestamp COLUMN and no
 *            rail at all; the phone had plain text rows with a bottom divider.
 *
 * WHY NO PER-ROW ARROWS, which was the other half of the question. The flow overview needs arrowheads
 * because it is a BRANCHING GRAPH — which way `Approved` and `Rejected` leave a node is genuinely
 * ambiguous. A timeline is one ordered list with no branch, so there is nothing to disambiguate, and every
 * activity feed people actually read (GitHub, Linear, Jira, Stripe) is dot-and-rail without them. Adding
 * arrows would be inventing a convention rather than following one.
 *
 * WHAT DOES make direction explicit is the pair of ANCHORS, which is what the owner chose instead: the rail
 * has a visible beginning above the first dot and a visible ending below the last.
 */

/** Tone → MUI palette token. The MEANING lives in `processTimelineTone`; only the paint is here. */
const DOT_COLOR: Record<ProcessTimelineTone, string> = {
  accent: 'primary.main',
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  neutral: 'action.disabled',
};

const RAIL = 'rgba(0,0,0,0.12)';
/** Left edge of the 1px connector inside an 8px rail column, so it runs through the dots' centres. */
const RAIL_X = 3.5;

export interface ProcessTimelineRow {
  id?: string | null;
  action?: string | null;
  outcome?: string | null;
  node_id?: string | null;
  notes?: string | null;
  created_at?: string | null;
  actor_name?: string | null;
  actor_email?: string | null;
}

export interface ProcessTimelineProps {
  /** Already filtered by `visibleTimelineEvents` — this component decides LOOK, never membership. */
  rows: ProcessTimelineRow[];
  /** The step name for a `node_id`, resolved by the caller because localisation differs per surface. */
  stepName: (nodeId: string | null | undefined) => string | undefined;
  /** The localized verb for an action, from the caller's own namespace. */
  actionLabel: (action: string | null | undefined) => string;
  locale: string;
  /**
   * Whether the request has ENDED. Drives the closing anchor, and it is a real question rather than
   * decoration: the last row is NOT the end when the request is still moving, and capping the rail there
   * would tell the reader it had finished.
   */
  terminal: boolean;
  /** "Submitted" / "Ongoing" / "Ended" — the anchor captions, from the caller's namespace. */
  labels: { start: string; ongoing: string; end: string };
}

export default function ProcessTimeline({
  rows, stepName, actionLabel, locale, terminal, labels,
}: ProcessTimelineProps) {
  if (rows.length === 0) return null;
  return (
    <Box>
      {/* START ANCHOR. A hollow ring rather than a filled dot: it marks where the rail BEGINS and is not
          itself an event — the first event row sits directly below it and carries its own dot. */}
      <Box sx={{ display: 'flex', gap: 1.5, pb: 1 }}>
        <Box sx={{ position: 'relative', width: 8, flexShrink: 0 }}>
          <Box sx={{
            position: 'absolute', top: 4, left: 0,
            width: 8, height: 8, borderRadius: '50%',
            border: '1.5px solid', borderColor: 'text.disabled',
          }} />
          <Box sx={{ position: 'absolute', top: 12, bottom: -1, left: RAIL_X, width: '1px', bgcolor: RAIL }} />
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: 0.4 }}>
          {labels.start}
        </Typography>
      </Box>

      {rows.map((event, index) => {
        const parts = processTimelineParts(stepName(event.node_id), event.notes);
        const actor = [event.actor_name, event.actor_email].filter(Boolean).join(' · ');
        return (
          <Box key={event.id ?? index} sx={{ display: 'flex', gap: 1.5, pb: 1.5 }}>
            {/* The rail column. `position: relative` so the connector spans this row's whole height
                INCLUDING the padding below it — the padding is the gap, so the line starts under its own
                dot and ends at the next one with nothing between rows for it to fail to cross. */}
            <Box sx={{ position: 'relative', width: 8, flexShrink: 0 }}>
              {/* The dot's colour says WHAT HAPPENED, never where the row sits. Keying it on the index
                  made two identical `Approved` events read as two different kinds of event. */}
              <Box sx={{
                position: 'absolute', top: 5, left: 0,
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: DOT_COLOR[processTimelineTone(event.action, event.outcome)],
              }} />
              <Box sx={{ position: 'absolute', top: 13, bottom: -1, left: RAIL_X, width: '1px', bgcolor: RAIL }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                {actionLabel(event.action)}
                {parts.step && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    — {parts.step}
                  </Typography>
                )}
                {parts.note && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    — {parts.note}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {event.created_at ? new Date(event.created_at).toLocaleString(locale) : ''}
                {actor ? ` · ${actor}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* END ANCHOR, and its shape is a FACT about the request rather than decoration.
          Terminal: a filled bar caps the rail — it stops here.
          Still moving: the rail fades out and the caption says so, because capping it would tell the reader
          the request had finished (svdy-ui §19 — the product has to be able to say which it is). */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ position: 'relative', width: 8, flexShrink: 0 }}>
          {terminal ? (
            <Box sx={{
              position: 'absolute', top: 5, left: 0,
              width: 8, height: 3, borderRadius: 0.5, bgcolor: 'text.disabled',
            }} />
          ) : (
            <Box sx={{
              position: 'absolute', top: 0, left: RAIL_X, width: '1px', height: 10,
              backgroundImage: `linear-gradient(${RAIL}, transparent)`,
            }} />
          )}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: 0.4 }}>
          {terminal ? labels.end : labels.ongoing}
        </Typography>
      </Box>
    </Box>
  );
}
